import { STORAGE_KEY } from "@constants/misc";
import logger from "@utils/logger";

const DEPOSIT_TRACKING_TTL_MS = 10 * 60 * 1000; // 10 minutes (matches watcher timeout)

export interface DepositTrackingMetadata {
  orderRef: string;
  startedAt: number;
}

/**
 * Persist deposit tracking state to sessionStorage
 * @param orderRef - The order reference ID
 * @param metadata - Additional metadata to persist
 */
export function persistDepositTracking(orderRef: string, metadata: Omit<DepositTrackingMetadata, "orderRef">): void {
  try {
    const data: DepositTrackingMetadata = {
      orderRef,
      ...metadata,
    };

    sessionStorage.setItem(STORAGE_KEY.PENDING_DEPOSIT_METADATA, JSON.stringify(data));

    logger._console.log("[DepositTracking] Persisted deposit tracking", {
      orderRef,
    });
  } catch (error) {
    logger._console.error("[DepositTracking] Failed to persist deposit tracking", error);
  }
}

/**
 * Get persisted deposit tracking state from sessionStorage
 * Returns null if not found, expired, or invalid
 */
export function getPersistedDepositTracking(): DepositTrackingMetadata | null {
  try {
    const stored = sessionStorage.getItem(STORAGE_KEY.PENDING_DEPOSIT_METADATA);
    if (!stored) {
      return null;
    }

    const data = JSON.parse(stored) as DepositTrackingMetadata;

    // Validate required fields
    if (!data.orderRef || !data.startedAt) {
      logger._console.warn("[DepositTracking] Invalid persisted data, missing required fields");
      clearDepositTracking();
      return null;
    }

    const age = Date.now() - data.startedAt;

    // Check if expired
    if (age > DEPOSIT_TRACKING_TTL_MS) {
      logger._console.log("[DepositTracking] Persisted tracking expired", {
        age,
        ageMinutes: Math.floor(age / 60000),
        orderRef: data.orderRef,
      });
      clearDepositTracking();
      return null;
    }

    logger._console.log("[DepositTracking] Retrieved persisted deposit tracking", {
      orderRef: data.orderRef,
      age,
      ageSeconds: Math.floor(age / 1000),
    });

    return data;
  } catch (error) {
    logger._console.error("[DepositTracking] Failed to load persisted tracking", error);
    clearDepositTracking();
    return null;
  }
}

/**
 * Clear persisted deposit tracking state from sessionStorage
 */
export function clearDepositTracking(): void {
  try {
    const stored = sessionStorage.getItem(STORAGE_KEY.PENDING_DEPOSIT_METADATA);
    if (stored) {
      const data = JSON.parse(stored) as DepositTrackingMetadata;
      logger._console.log("[DepositTracking] Clearing deposit tracking", {
        orderRef: data.orderRef,
      });
    }

    sessionStorage.removeItem(STORAGE_KEY.PENDING_DEPOSIT_METADATA);
  } catch (error) {
    logger._console.error("[DepositTracking] Failed to clear deposit tracking", error);
  }
}

/**
 * Check if deposit tracking has expired based on timestamp
 * @param startedAt - The timestamp when tracking started
 * @returns true if expired, false otherwise
 */
export function isDepositTrackingExpired(startedAt: number): boolean {
  const age = Date.now() - startedAt;
  return age > DEPOSIT_TRACKING_TTL_MS;
}

/**
 * Get the TTL in milliseconds
 */
export function getDepositTrackingTTL(): number {
  return DEPOSIT_TRACKING_TTL_MS;
}
